import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Card,
    CardContent,
    Grid,
    Chip,
    useTheme as useMuiTheme,
    Divider
} from '@mui/material';
import { Close as CloseIcon, CheckCircle as ActiveIcon, Cancel as InactiveIcon } from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';

interface City {
    id: number;
    name: string;
    code: string;
    zone: string;
    status: number;
    created_at: string;
    updated_at: string;
}

interface CityViewProps {
    open: boolean;
    city: City;
    onClose: () => void;
}

const CityView: React.FC<CityViewProps> = ({ open, city, onClose }) => {
    const muiTheme = useMuiTheme();

    const getStatusChip = (status: number) => {
        return status === 1 ? (
            <Chip
                icon={<ActiveIcon />}
                label="Active"
                color="success"
                size="small"
                variant="outlined"
            />
        ) : (
            <Chip
                icon={<InactiveIcon />}
                label="Inactive"
                color="error"
                size="small"
                variant="outlined"
            />
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: muiTheme.palette.background.paper,
                    border: `1px solid ${muiTheme.palette.divider}`
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: `1px solid ${muiTheme.palette.divider}`,
                pb: 2
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    City Details
                </Typography>
                <Button
                    onClick={onClose}
                    sx={{ minWidth: 'auto', p: 1 }}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ 
                        fontWeight: 700, 
                        color: muiTheme.palette.text.primary,
                        mb: 1
                    }}>
                        {city.name}
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        City Code: {city.code}
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="h6" sx={{ 
                            mb: 2, 
                            color: muiTheme.palette.text.primary, 
                            fontWeight: 600 
                        }}>
                            Basic Information
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: muiTheme.palette.background.paper, border: `1px solid ${muiTheme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    City Name
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {city.name}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    City Code
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {city.code}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Zone
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {city.zone}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: muiTheme.palette.background.paper, border: `1px solid ${muiTheme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Status
                                </Typography>
                                <Box sx={{ mb: 1 }}>
                                    {getStatusChip(city.status)}
                                </Box>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Created Date
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {new Date(city.created_at).toLocaleDateString()}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Last Updated
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {new Date(city.updated_at).toLocaleDateString()}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Additional Information */}
                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ 
                            mb: 2, 
                            color: muiTheme.palette.text.primary, 
                            fontWeight: 600 
                        }}>
                            Additional Information
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Card sx={{ bgcolor: muiTheme.palette.background.paper, border: `1px solid ${muiTheme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    City ID
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {city.id}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Created At
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {new Date(city.created_at).toLocaleString()}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Updated At
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {new Date(city.updated_at).toLocaleString()}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ 
                p: 3, 
                pt: 2,
                borderTop: `1px solid ${muiTheme.palette.divider}`
            }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CityView;
